import React, { useContext, useState, useEffect, useRef } from "react";
import "../../styles/global.css";
import { TranslationsContext } from "../../utils/TranslationsContext";
import AddPicture from "../../components/Modal/components/AddPicture";
import { apiRoutes } from "../../utils/apiRoutes";
import AccreditationInput from "../../components/AccreditationInput/AccreditationInput";
import { formatDateForInput } from "../../utils/formatDateForInput";
import AccreditationButton from "../../components/AccreditationButton/AccreditationButton";
import { useAlert } from "../../utils/AlertContext";
import { validateString } from "../../utils/validateString";
import { trimValues } from "../../utils/trimValues";
import { createPublicData, fetchData } from "../../utils/methods";
import Loader from "../../components/Loader/Loader";
import "./styles/styles.css";
import { images, icons } from "../../constants";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import CustomDateRangePicker from "../../components/CustomDateRangePicker/CustomDateRangePicker";
import { formatDateTimeBg, formatDateTimeRangeBg } from "../../utils/belgradeTime";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
// reCAPTCHA is disabled outside production — a dev build has no
// domain-verified site key, and gs.rest skips verification in dev too
// (see rest.js/postRoutes.js /upload and /personsrecaptchav3).
const IS_DEV = process.env.NODE_ENV !== "production";

const PressPage = () => {
  const { id } = useParams();
  const { t } = useContext(TranslationsContext);
  const ID_CARD_AND_PASSPORT = [
    { key: 1, value: "licna_karta", label: t("IDENTITY_CARD") },
    { key: 2, value: "pasos", label: t("PASSPORT") },
  ];
  const ID_CARD_AND_NATIONAL_ID = [
    { key: 1, value: "licna_karta", label: t("IDENTITY_CARD") },
    { key: 2, value: "jmbg", label: t("JMBG") },
  ];
  const PRIVILEGED_COUNTRIES = [
    "COUNTRY_BOSNIA_AND_HERZEGOVINA",
    "COUNTRY_MONTENEGRO",
  ];

  const [personObject, setPersonObject] = useState({
    slika: "",
    ime: "",
    prezime: "",
    entitet: "",
    pozicija: "",
    email: "",
    tip_dokumenta: ID_CARD_AND_NATIONAL_ID[0].value,
    broj_dokumenta: "",
    slika_crop: "",
    drzavljanstvo: "",
  });

  const [imageLoading, setImageLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const recaptchaLoadedRef = useRef(false);
  const [eventInfo, setEventInfo] = useState(null);

  useEffect(() => {});

  // Load reCAPTCHA script
  useEffect(() => {
    if (IS_DEV) return;

    if (!RECAPTCHA_SITE_KEY) {
      console.warn(
        "REACT_APP_RECAPTCHA_SITE_KEY is not set in environment variables",
      );
      return;
    }

    if (recaptchaLoadedRef.current) return;

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      recaptchaLoadedRef.current = true;
      console.log("reCAPTCHA v3 loaded successfully");
    };
    script.onerror = () => {
      console.error("Failed to load reCAPTCHA script");
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts
      const existingScript = document.querySelector(
        `script[src*="recaptcha/api.js"]`,
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  useEffect(() => {
    const language = localStorage.getItem("language") || "sr";

    const fetchCountries = async () => {
      setIsLoading(true);
      try {
        const result = await fetchData(
          `${apiRoutes.getCountries}?language=${language}`,
          showAlert,
          navigate,
          t,
          false,
        );

        if (!result?.data || !Array.isArray(result.data)) {
          showAlert?.(t("FETCH_FAILED"), "error");
          return;
        }

        setCountries(result.data);
      } catch (err) {
        console.error("Unexpected error in fetchCountries:", err);
        showAlert?.(t?.("NETWORK_ERROR") || "Network error", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCountries();
  }, [navigate, showAlert, t]);

  useEffect(() => {
    if (!id || !apiRoutes.eventByLink) return;
    let cancelled = false;

    const fetchEventInfo = async () => {
      try {
        const result = await fetchData(
          apiRoutes.eventByLink.replace(":link", id),
          showAlert,
          navigate,
          t,
          false,
        );
        if (!result?.data || cancelled) return;
        setEventInfo(result.data);
      } catch (err) {
        console.error("Failed to fetch event by link:", err);
      }
    };

    fetchEventInfo();
    return () => {
      cancelled = true;
    };
  }, [id, navigate, showAlert, t]);

  const setInputValue = (key, value) => {
    setPersonObject((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
  };

  console.log("countries", countries);

  // Same rules as the AddPerson modal (components/Modal/components/AddPerson.jsx)
  // — this form posts the same person shape, so the two must stay in step.

  // Outside Serbia and the privileged countries only a passport is accepted;
  // the dropdown already shows "pasos", the value has to follow.
  useEffect(() => {
    const isNotPrivilegedAndNotSerbia =
      !PRIVILEGED_COUNTRIES.includes(personObject.drzavljanstvo) &&
      personObject.drzavljanstvo !== "COUNTRY_SERBIA" &&
      personObject.drzavljanstvo !== "";
    if (isNotPrivilegedAndNotSerbia && personObject.tip_dokumenta !== "pasos") {
      setInputValue("tip_dokumenta", "pasos");
    }
  }, [personObject.drzavljanstvo]);

  // Issuing country / issue / expiry dates only apply to a document, not to
  // a JMBG — hidden on that path, so cleared too.
  const showIssuanceFields =
    !!personObject.drzavljanstvo &&
    (personObject.drzavljanstvo !== "COUNTRY_SERBIA" ||
      (!personObject.jmbg &&
        !!(personObject.br_licne_karte || personObject.datum_rodjenja)));

  const handleJmbgChange = (value) => {
    setInputValue("jmbg", value);
    if (value) {
      setInputValue("br_licne_karte", "");
      setInputValue("datum_rodjenja", "");
      setInputValue("zemlja_izdavanja", "");
      setInputValue("datum_izdavanja", "");
      setInputValue("datum_isteka", "");
    }
  };

  const handleIdNumberChange = (value) => {
    setInputValue("br_licne_karte", value);
    if (value || personObject.datum_rodjenja) {
      setInputValue("jmbg", "");
    }
  };

  const handleBirthDateChange = (value) => {
    setInputValue("datum_rodjenja", value);
    if (value || personObject.br_licne_karte) {
      setInputValue("jmbg", "");
    }
  };

  const validateAllFields = () => {
    const fieldsToValidate = [
      "ime",
      "prezime",
      "slika",
      "broj_dokumenta",
      "email",
      "jmbg",
      "drzavljanstvo",
      "datum_rodjenja",
      ...(showIssuanceFields
        ? ["zemlja_izdavanja", "datum_izdavanja", "datum_isteka"]
        : []),
      "tip_dokumenta",
    ];
    let hasError = false;
    let err = false;
    fieldsToValidate.forEach((field) => {
      let value = personObject[field];
      if (field === "broj_dokumenta") {
        err = validateString(value, "number");
      } else if (field === "jmbg") {
        if (personObject.jmbg && personObject.jmbg !== "") {
          err = validateString(value, "jmbg");
        } else {
          err = false;
          value = "1";
        }
      } else if (field === "datum_izdavanja") {
        const izdavanje = personObject.datum_izdavanja;
        const istek = personObject.datum_isteka;

        if (izdavanje && istek) {
          const datumIzdavanja = new Date(izdavanje);
          const datumIsteka = new Date(istek);

          if (!isNaN(datumIzdavanja) && !isNaN(datumIsteka)) {
            if (datumIzdavanja >= datumIsteka) {
              console.warn("Datum izdavanja mora biti pre datuma isteka");

              setErrors((prevErrors) => ({
                ...prevErrors,
                datum_izdavanja: true,
                datum_isteka: true,
              }));

              hasError = true;
              return;
            }
          }
        }
      } else if (field === "datum_rodjenja") {
        // Only the Serbian-citizen "ID card number" path renders a date-of-birth
        // input (see the JMBG/ID-card block below) — foreigners and the JMBG
        // path never collect it, so it must not be required there either.
        if (personObject.jmbg || personObject.drzavljanstvo !== "COUNTRY_SERBIA") {
          err = false;
          value = "1";
        }
      } else if (field === "email") {
        if (value !== "") {
          err = validateString(value, "e_mail");
        } else {
          return;
        }
      }
      if (!value || err) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: true,
        }));
        hasError = true;
      }
    });

    return !hasError;
  };

  const handleAddClick = async () => {
    if (!personObject.slika) {
      showAlert(t("ERROR_PHOTO_REQUIRED"), "error");
      return;
    }

    if (eventInfo) {
      const now = new Date();
      const from = eventInfo.prijava_od ? new Date(eventInfo.prijava_od) : null;
      const to = eventInfo.prijava_do ? new Date(eventInfo.prijava_do) : null;
      if ((from && !isNaN(from) && now < from) || (to && !isNaN(to) && now > to)) {
        return;
      }
    }
    if (personObject.drzavljanstvo === "" && countries.length === 0) {
      showAlert(t("CITIZENSHIP_INPUT_FIELD_ERROR"), "error");
      return;
    }
    if (personObject.drzavljanstvo === "COUNTRY_SERBIA") {
      if (personObject.jmbg) {
        personObject.tip_dokumenta = "jmbg";
        personObject.broj_dokumenta = personObject.jmbg;
      } else {
        personObject.tip_dokumenta = "licna_karta";
        personObject.broj_dokumenta = personObject.br_licne_karte;
      }
    }
    setErrors();
    const isValid = validateAllFields();
    if (!isValid) {
      showAlert(
        t(
          personObject.jmbg && validateString(personObject.jmbg, "jmbg")
            ? "ERROR_INVALID_JMBG"
            : "ERROR_MISSING_REQUIRED_FIELDS",
        ),
        "error",
      );
    }
    console.log("errors", errors);

    console.log("countries.length", countries.length);

    if (isValid) {
      setIsLoading(true);
      if (personObject.drzavljanstvo === "COUNTRY_SERBIA") {
        // Date of birth belongs to the ID-card path only.
        if (personObject.jmbg) delete personObject.datum_rodjenja;
        delete personObject.br_licne_karte;
        delete personObject.jmbg;
      } else {
        delete personObject.datum_rodjenja;
      }
      personObject.slika_avatar = personObject.slika_crop;
      const sendData = trimValues(personObject);
      sendData.linkHash = id;

      // Execute reCAPTCHA v3
      if (!IS_DEV && RECAPTCHA_SITE_KEY && window.grecaptcha) {
        try {
          const recaptchaToken = await window.grecaptcha.execute(
            RECAPTCHA_SITE_KEY,
            { action: "submit_press_form" },
          );
          sendData.recaptchaToken = recaptchaToken;
          console.log("reCAPTCHA token obtained");
        } catch (error) {
          console.error("reCAPTCHA execution error:", error);
          // Continue with submission even if reCAPTCHA fails
        }
      }

      console.log("token", sendData);

      try {
        console.log("Slanje podataka:", sendData);
        const response = await createPublicData(
          "/personsrecaptchav3",
          sendData,
          t,
        );

        if (!response?.error) {
          console.log("Podaci su uspešno sačuvani:", response);
          showAlert(t("SUCCESSFULLY_SAVED"), "success");
        } else {
          showAlert(response?.error?.code, "error");
          console.error("Greška prilikom čuvanja podataka: ", response);
        }
      } catch (error) {
        console.error("API poziv nije uspeo:", error.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log("Postoje greške u formi.");
      showAlert(t("FORM_HAS_ERRORS") || "Proverite obavezna polja.", "error");
    }
  };

  useEffect(() => {
    // Handle Enter key to trigger save button
    const handleEnterKey = (event) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        // Don't trigger if user is typing in a textarea or input
        const target = event.target;
        if (
          target.tagName === "TEXTAREA" ||
          (target.tagName === "INPUT" && target.type !== "submit")
        ) {
          return;
        }
        event.preventDefault();
        handleAddClick();
      }
    };

    document.addEventListener("keydown", handleEnterKey);

    return () => {
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [personObject]);

  if (isLoading || imageLoading) {
    return <Loader />;
  }

  const getRegistrationStatus = () => {
    if (!eventInfo) return "unknown";
    const now = new Date();
    const from = eventInfo.prijava_od ? new Date(eventInfo.prijava_od) : null;
    const to = eventInfo.prijava_do ? new Date(eventInfo.prijava_do) : null;
    if (from && !isNaN(from) && now < from) return "not_open";
    if (to && !isNaN(to) && now > to) return "closed";
    return "open";
  };

  const registrationStatus = getRegistrationStatus();
  const isFormVisible =
    registrationStatus === "open" || registrationStatus === "unknown";

  return (
    <div className="flex-direction-column gap-36 items-center height-100 press-page">
      <div className="flex-direction-column gap-12 items-center press-page-header">
        <div className="flex-direction-row gap-18 items-center press-page-brand">
          <img src={images.logo} alt="logo" className="flag" />
          {/* Subtitle sits directly under the title, beside the emblem. */}
          <div className="flex-direction-column gap-4 press-page-brand-text">
            <h1 className="font-36 inria-sans-300 login-title">
              {t("ACCREDITATION_TITLE")}
            </h1>
            <p className="inria-sans-400 font-16 press-page-subtitle">
              {t("ADD_PRESS_PERSON_TITLE")}
            </p>
          </div>
        </div>
        {eventInfo && (
          <div className="press-event-card">
            <h2 className="press-event-card__title inria-sans-700">
              {eventInfo.naziv}
            </h2>
            <div className="press-event-card__grid">
              {(eventInfo.trajanje_od || eventInfo.trajanje_do) && (
                <div className="press-event-card__item">
                  <div className="press-event-card__icon">
                    <img
                      src={icons.calendar}
                      width={18}
                      height={18}
                      alt=""
                    />
                  </div>
                  <div className="press-event-card__item-text">
                    <span className="press-event-card__label inria-sans-700">
                      {t("EVENT_DURATION") || "Event"}
                    </span>
                    <span className="press-event-card__value inria-sans-700">
                      {formatDateTimeRangeBg(eventInfo.trajanje_od, eventInfo.trajanje_do)}
                    </span>
                  </div>
                </div>
              )}

              {eventInfo.lokacija && (
                <div className="press-event-card__item">
                  <div className="press-event-card__icon">
                    <img
                      src={icons.location_pin}
                      width={18}
                      height={18}
                      alt=""
                    />
                  </div>
                  <div className="press-event-card__item-text">
                    <span className="press-event-card__label inria-sans-700">
                      {t("LOCATION") || "Location"}
                    </span>
                    <span className="press-event-card__value inria-sans-700">
                      {eventInfo.lokacija}
                    </span>
                  </div>
                </div>
              )}

              {(eventInfo.prijava_od || eventInfo.prijava_do) && (
                <div className="press-event-card__item press-event-card__item--accent">
                  <div className="press-event-card__icon">
                    <img
                      src={icons.accreditation}
                      width={18}
                      height={18}
                      alt=""
                    />
                  </div>
                  <div className="press-event-card__item-text">
                    <span className="press-event-card__label inria-sans-700">
                      {t("REGISTRATION_PERIOD") || "Registration"}
                    </span>
                    <span className="press-event-card__value inria-sans-700">
                      {formatDateTimeRangeBg(eventInfo.prijava_od, eventInfo.prijava_do)}
                    </span>
                  </div>
                </div>
              )}

              {registrationStatus !== "unknown" && (
                <div
                  className={`press-event-card__item press-event-card__item--status press-event-card__item--${registrationStatus}`}
                  role="status"
                >
                  <div className="press-event-card__icon">
                    <span className="press-status-dot" aria-hidden="true" />
                  </div>
                  <div className="press-event-card__item-text">
                    <span className="press-event-card__label inria-sans-700">
                      {t("REGISTRATIONS_LABEL") || "Prijave"}
                    </span>
                    <span className="press-event-card__value press-event-card__value--status inria-sans-700">
                      {registrationStatus === "open"
                        ? t("REGISTRATION_STATUS_OPEN") || "Otvorene"
                        : registrationStatus === "not_open"
                          ? t("REGISTRATION_STATUS_NOT_OPEN") ||
                            "Još nisu otvorene"
                          : t("REGISTRATION_STATUS_CLOSED") || "Zatvorene"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {!isFormVisible && (
        <div
          className={`press-status-card press-status-card--${registrationStatus}`}
        >
          <div className="press-status-card__icon">
            <img
              src={icons.calendar}
              width={28}
              height={28}
              alt=""
            />
          </div>
          <h3 className="press-status-card__title inria-sans-700 font-20">
            {registrationStatus === "not_open"
              ? t("REGISTRATION_NOT_OPEN_YET") ||
                "Prijave još uvek nisu otvorene"
              : t("REGISTRATION_CLOSED") || "Prijave su zatvorene"}
          </h3>
          <p className="press-status-card__text inria-sans-400 font-14">
            {registrationStatus === "not_open"
              ? `${t("REGISTRATION_OPENS") || "Prijave se otvaraju"}: ${formatDateTimeBg(eventInfo?.prijava_od)}`
              : `${t("REGISTRATION_CLOSED_ON") || "Prijave su zatvorene dana"}: ${formatDateTimeBg(eventInfo?.prijava_do)}`}
          </p>
        </div>
      )}
      {isFormVisible && (
      <div className="flex-direction-column gap-48 press-page-modal">
        <div className="flex-direction-column gap-32 width-100 press-modal-content">
          <div className="flex-direction-row gap-24 press-modal-wrapper">
            <div className="flex-direction-column gap-24 press-page-modal-side">
              <p className="press-section-title inria-sans-700 font-12">
                Lični podaci
              </p>
              <div className="flex-direction-column gap-8 items-center press-photo-zone">
                <AddPicture
                  setImageLoading={setImageLoading}
                  setImagePreview={(value) => {
                    setInputValue("slika_crop", value);
                  }}
                  imagePreview={personObject.slika_crop}
                  setInputValue={(value) => setInputValue("slika", value)}
                  route={apiRoutes.validateImage}
                  getUploadHeaders={async () => {
                    const headers = {};
                    if (id) headers["x-link-hash"] = id;
                    if (!IS_DEV && RECAPTCHA_SITE_KEY && window.grecaptcha) {
                      try {
                        const token = await window.grecaptcha.execute(
                          RECAPTCHA_SITE_KEY,
                          { action: "upload_press_image" },
                        );
                        if (token) headers["x-recaptcha-token"] = token;
                      } catch (err) {
                        console.error("reCAPTCHA execution error:", err);
                      }
                    }
                    return headers;
                  }}
                />
              </div>
              <div className="flex-direction-row gap-48 width-100 modal-two-inputs-inline">
                <AccreditationInput
                  label={t("NAME")}
                  className="modal-input"
                  setInputValue={(value) => setInputValue("ime", value)}
                  value={personObject.ime}
                  error={errors?.ime}
                  required
                />
                <AccreditationInput
                  label={t("SURNAME")}
                  className="modal-input"
                  setInputValue={(value) => setInputValue("prezime", value)}
                  value={personObject.prezime}
                  error={errors?.prezime}
                  required
                />
              </div>
              <div className="width-100">
                <AccreditationInput
                  className="modal-input"
                  label={t("EMAIL")}
                  setInputValue={(value) => setInputValue("email", value)}
                  value={personObject.email}
                  error={errors?.email}
                />
              </div>
            </div>
            <div className="flex-direction-column gap-24 press-page-modal-side">
              <p className="press-section-title inria-sans-700 font-12">
                Dokument i pozicija
              </p>
              <div className="width-100">
                <AccreditationInput
                  className="modal-input"
                  label={t("POSITION")}
                  setInputValue={(value) => setInputValue("pozicija", value)}
                  value={personObject.pozicija}
                />
              </div>
              <div className="width-100">
                <AccreditationInput
                  className="modal-input"
                  label={t("ENTITY")}
                  setInputValue={(value) => setInputValue("entitet", value)}
                  value={personObject.entitet}
                />
              </div>
              {countries && countries.length > 0 && (
                <AccreditationInput
                  type="dropdown"
                  label={t("CITIZENSHIP")}
                  className="modal-input"
                  searchable={true}
                  selectedOption={
                    countries.find(
                      (c) => c.country === personObject?.drzavljanstvo,
                    )?.name || ""
                  }
                  setSelectedOption={(value) => {
                    const selectedKey = countries?.find(
                      (option) => option.country === value,
                    )?.country;
                    setInputValue("drzavljanstvo", selectedKey);
                  }}
                  options={countries?.map((option) => ({
                    key: option.country,
                    value: option.name,
                    flag: option.flag,
                  }))}
                  flags
                  error={errors?.drzavljanstvo}
                  required
                />
              )}
              {personObject?.drzavljanstvo && (
                <div
                  className={`document-section ${
                    errors?.broj_dokumenta &&
                    personObject?.drzavljanstvo === "COUNTRY_SERBIA"
                      ? "error"
                      : ""
                  } relative`}
                >
                  {personObject?.drzavljanstvo === "COUNTRY_SERBIA" ? (
                    <div className="flex-direction-row gap-12 relative">
                      <div className="flex-direction-column gap-24 width-100">
                        <div
                          className={`width-100 input-block ${
                            personObject.jmbg ? "active" : ""
                          }`}
                        >
                          <AccreditationInput
                            label={t("JMBG")}
                            value={personObject.jmbg}
                            setInputValue={(value) => handleJmbgChange(value)}
                            disabled={
                              !!personObject.br_licne_karte ||
                              !!personObject.datum_rodjenja
                            }
                            className="modal-input"
                            error={errors?.jmbg}
                          />
                        </div>

                        <div
                          className={`flex-direction-row gap-48 width-100 modal-two-inputs-inline`}
                        >
                          <AccreditationInput
                            label={t("ID_CARD_NUMBER")}
                            className="modal-input"
                            value={personObject.br_licne_karte}
                            setInputValue={(value) =>
                              handleIdNumberChange(value)
                            }
                            disabled={personObject.jmbg}
                          />

                          {/* Same date-only picker as the AddPerson modal. */}
                          <CustomDateRangePicker
                            label={t("DATE_OF_BIRTH")}
                            className="modal-input up-calendar"
                            mode="single"
                            dateRange={
                              personObject.datum_rodjenja
                                ? formatDateForInput(
                                    personObject.datum_rodjenja,
                                  )
                                : ""
                            }
                            setDateRange={(value) => {
                              handleBirthDateChange(
                                new Date(value).toISOString(),
                              );
                            }}
                            disabled={!!personObject.jmbg}
                            error={errors?.datum_rodjenja}
                          />
                        </div>
                      </div>
                      <div className="flex-direction-column relative  or-separator ">
                        <div></div>
                        <span className="font-12 inria-sans-700">
                          {t("OR")}
                        </span>

                        <div></div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-direction-row gap-48 width-100 modal-two-inputs-inline">
                      <AccreditationInput
                        type="dropdown"
                        label={t("DOCUMENT_TYPE")}
                        className="modal-input"
                        selectedOption={
                          PRIVILEGED_COUNTRIES.some((country) =>
                            personObject.drzavljanstvo
                              .toLowerCase()
                              ?.includes(country.toLowerCase()),
                          )
                            ? personObject?.tip_dokumenta?.replace(/_/g, " ")
                            : "pasos"
                        }
                        setSelectedOption={(value) => {
                          const selectedKey = ID_CARD_AND_PASSPORT.find(
                            (option) => option.key === value,
                          )?.value;
                          setInputValue("tip_dokumenta", selectedKey);
                        }}
                        options={ID_CARD_AND_PASSPORT.map((option) => ({
                          key: option.key,
                          value: option.label,
                        }))}
                        disabled={
                          !PRIVILEGED_COUNTRIES.some((country) =>
                            personObject.drzavljanstvo
                              .toLowerCase()
                              ?.includes(country.toLowerCase()),
                          )
                        }
                        error={errors?.tip_dokumenta}
                        required={PRIVILEGED_COUNTRIES.some((country) =>
                          personObject.drzavljanstvo
                            .toLowerCase()
                            ?.includes(country.toLowerCase()),
                        )}
                      />
                      <AccreditationInput
                        label={t("DOCUMENT_NUMBER")}
                        className="modal-input"
                        setInputValue={(value) =>
                          setInputValue("broj_dokumenta", value)
                        }
                        value={personObject.broj_dokumenta}
                        error={errors?.broj_dokumenta}
                        required
                      />
                    </div>
                  )}
                </div>
              )}
              {countries && countries.length > 0 && showIssuanceFields && (
                <AccreditationInput
                  type="dropdown"
                  label={t("ISSUING_COUNTRY")}
                  className="modal-input"
                  searchable={true}
                  selectedOption={
                    countries.find(
                      (c) => c.country === personObject?.zemlja_izdavanja,
                    )?.name || ""
                  }
                  setSelectedOption={(value) => {
                    const selectedKey = countries?.find(
                      (option) => option.country === value,
                    )?.country;
                    setInputValue("zemlja_izdavanja", selectedKey);
                  }}
                  options={countries?.map((option) => ({
                    key: option.country,
                    value: option.name,
                    flag: option.flag,
                  }))}
                  flags
                  error={errors?.zemlja_izdavanja}
                  required
                />
              )}{" "}
              {showIssuanceFields && (
                <div className="flex-direction-row gap-48 width-100 modal-two-inputs-inline">
                  <CustomDateRangePicker
                    label={t("DATE_OF_ISSUE")}
                    required
                    mode="single"
                    dateRange={
                      personObject.datum_izdavanja
                        ? formatDateForInput(personObject.datum_izdavanja)
                        : ""
                    }
                    setDateRange={(value) => {
                      setInputValue(
                        "datum_izdavanja",
                        new Date(value).toISOString(),
                      );
                    }}
                    error={errors?.datum_izdavanja}
                  />
                  <CustomDateRangePicker
                    label={t("EXPIRY_DATE")}
                    required
                    mode="single"
                    dateRange={
                      personObject.datum_isteka
                        ? formatDateForInput(personObject.datum_isteka)
                        : ""
                    }
                    setDateRange={(value) => {
                      setInputValue(
                        "datum_isteka",
                        new Date(value).toISOString(),
                      );
                    }}
                    error={errors?.datum_isteka}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-direction-row justify-center gap-18 width-100 press-actions">
          <AccreditationButton
            type="add-confirm"
            title={t("REGISTER_SUBMIT") || "Prijavi se"}
            className="modal-save-btn press-save-btn"
            onClick={handleAddClick}
            classNameWrapper="width-100"
          />
        </div>
      </div>
      )}
    </div>
  );
};
export default PressPage;
